// Sentimentals Service for Automated Birthday/Holiday SMS
import { showToast } from "../utils/show-toast";
import { shouldToastApiError } from '@/app/_module/utils/apiResponseHandler';
import { normalizeApiError } from "../utils/apiResponseHandler";

import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";

// Type definitions
export type OccasionType = 'birthday' | 'policy_anniversary' | 'christmas' | 'thanksgiving' | 'new_year' | 'lapsed_policy';
export type AutomationMode = 'manual' | 'auto';
export type ScheduleStatus = 'pending' | 'pending_approval' | 'approved' | 'rejected' | 'sent' | 'failed';
export type MessageStatus = 'success' | 'failed' | 'pending';

export interface SentimentalSettings {
  id?: string;
  userId?: string;
  agencyId?: string;
  enabled: boolean;
  automationMode: AutomationMode;
  sendTime: string; // HH:mm format
  timezone: string;
  scope: 'personal' | 'agency';

  // Individual occasion settings
  birthdayEnabled: boolean;
  birthdayTemplateId?: string;
  birthdayMessage?: string;

  policyAnniversaryEnabled: boolean;
  policyAnniversaryTemplateId?: string;
  policyAnniversaryMessage?: string;

  christmasEnabled: boolean;
  christmasTemplateId?: string;
  christmasMessage?: string;
  christmasDate?: string; // MM-DD format

  thanksgivingEnabled: boolean;
  thanksgivingTemplateId?: string;
  thanksgivingMessage?: string;

  newYearEnabled: boolean;
  newYearTemplateId?: string;
  newYearMessage?: string;

  lapsedPolicyEnabled: boolean;
  lapsedPolicyMessage?: string;
  lapsedPolicyDeliveryChannel?: string;

  defaultDeliveryChannel?: string;
  defaultVoiceCloneId?: string;

  createdAt?: string;
  updatedAt?: string;
}

export interface SentimentalSchedule {
  id: string;
  userId: string;
  leadId: string;
  leadName: string;
  leadPhone: string;
  leadState?: string; // US state for timezone calculation
  occasionType: OccasionType;
  occasionDate: string;
  scheduledDate: string;
  scheduledTime: string;
  message: string;
  templateId?: string;
  status: ScheduleStatus;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SentimentalLog {
  id: string;
  userId: string;
  leadId: string;
  leadName: string;
  leadPhone: string;
  occasionType: OccasionType;
  message: string;
  status: MessageStatus;
  sentAt: string;
  errorMessage?: string;
  messageSid?: string;
  cost?: number;
  createdAt: string;
}

export interface GetSchedulesParams {
  status?: ScheduleStatus;
  occasionType?: OccasionType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface GetLogsParams {
  status?: MessageStatus;
  occasionType?: OccasionType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface TestSendRequest {
  templateId: string;
  leadId: string;
  occasionType: OccasionType;
}

export interface UpcomingOccasion {
  id: string;
  leadId: string;
  leadName: string;
  occasion: string;
  date: string;
  daysAway: number;
}

export interface SentimentalSettingsResponse {
  statusCode: number;
  message: string;
  data: SentimentalSettings;
  timestamp: string;
}

export interface SentimentalSchedulesResponse {
  statusCode: number;
  message: string;
  data: {
    items: SentimentalSchedule[];
    meta: {
      totalItems: number;
      page: number;
      totalPages: number;
      itemCount: number;
      limit: number;
    };
  };
  timestamp: string;
}

export interface SentimentalLogsResponse {
  statusCode: number;
  message: string;
  data: {
    items: SentimentalLog[];
    meta: {
      totalItems: number;
      page: number;
      totalPages: number;
      itemCount: number;
      limit: number;
    };
  };
  timestamp: string;
}

export interface StandardApiResponse {
  statusCode: number;
  message: string;
  data?: any;
  timestamp: string;
}

// Service class
class SentimentalsService {
  // Handle errors consistently
  private handleError(error: unknown, operation: string): never {
    const normalized = normalizeApiError(error);
    const message = normalized.message || `Failed to ${operation}`;
    const retrySuffix = typeof normalized.retryAfterSeconds === 'number'
      ? ` (retry after ${normalized.retryAfterSeconds}s)`
      : '';
    // silent = MFA-window 403 the interceptor self-recovers — never toast it
    if (shouldToastApiError(normalized, operation)) { showToast(`${message}${retrySuffix}`, "error") }

    const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    throw e;
  }

  // Transform frontend settings to backend format
  private transformSettingsToBackend(settings: any) {
    // Convert sendTime from HH:mm to HH:mm:ss format if needed
    let sendTime = settings.sendTime;
    if (sendTime && sendTime.length === 5) {
      sendTime = `${sendTime}:00`;
    }

    return {
      ...settings,
      sendTime,
      autoSend: settings.automationMode === 'auto',
    };
  }

  // Transform backend settings to frontend format
  private transformSettingsFromBackend(settings: any) {
    // Convert sendTime from HH:mm:ss to HH:mm format for the time input
    let sendTime = settings.sendTime;
    if (sendTime && sendTime.length === 8) {
      sendTime = sendTime.substring(0, 5);
    }

    return {
      ...settings,
      sendTime,
      automationMode: settings.autoSend ? 'auto' : 'manual',
    };
  }

  // Get user's sentimental settings
  async getSettings(): Promise<SentimentalSettings> {
    try {
      const url = getApiUrl(endpoint.sentimentals.getSettings);

      const response = await api.get<SentimentalSettingsResponse>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      const apiResponse = response as unknown as SentimentalSettingsResponse;

      if (!apiResponse?.data) {
        throw new Error('Invalid response from sentimentals settings service');
      }

      // Transform backend response to frontend format
      return this.transformSettingsFromBackend(apiResponse.data);
    } catch (error) {
      this.handleError(error, 'fetch sentimental settings');
    }
  }

  // Update sentimental settings
  async updateSettings(data: Partial<SentimentalSettings>): Promise<SentimentalSettings> {
    try {
      // Validate required fields if enabled
      if (data.enabled !== false) {
        if (!data.sendTime) {
          throw new Error('Send time is required when enabled');
        }
        if (!data.timezone) {
          throw new Error('Timezone is required when enabled');
        }
      }

      const url = getApiUrl(endpoint.sentimentals.updateSettings);

      // Transform frontend data to backend format
      const backendData = this.transformSettingsToBackend(data);

      const response = await api.patch({
        url,
        body: backendData,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      const apiResponse = response as unknown as SentimentalSettingsResponse;
      if (!apiResponse?.data) {
        throw new Error('Invalid response from sentimentals settings service');
      }

      showToast("Sentimental settings updated successfully", "success");
      // Transform backend response to frontend format
      return this.transformSettingsFromBackend(apiResponse.data);
    } catch (error) {
      this.handleError(error, 'update sentimental settings');
    }
  }

  // Get scheduled messages
  async getSchedules(params: GetSchedulesParams = {}): Promise<SentimentalSchedule[]> {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append('status', params.status);
      if (params.occasionType) queryParams.append('occasionType', params.occasionType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const url = `${getApiUrl(endpoint.sentimentals.getSchedules)}?${queryParams.toString()}`;

      const response = await api.get<SentimentalSchedulesResponse>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      // Backend returns: { statusCode, message, data: schedules[], timestamp }
      const apiResponse = response as unknown as { statusCode: number; message: string; data: SentimentalSchedule[]; timestamp: string };

      // Return the schedules array directly (or empty array if no data)
      return apiResponse?.data || [];
    } catch (error) {
      this.handleError(error, 'fetch sentimental schedules');
    }
  }

  // Approve a single scheduled message
  async approveSchedule(id: string): Promise<void> {
    try {
      if (!id?.trim()) {
        throw new Error('Schedule ID is required');
      }

      const url = getApiUrl(endpoint.sentimentals.approveSchedule(id));

      await api.post<StandardApiResponse>({
        url,
        auth: true,
      });

      showToast("Message approved successfully", "success");
    } catch (error) {
      this.handleError(error, 'approve schedule');
    }
  }

  // Reject/skip a scheduled message
  async rejectSchedule(id: string): Promise<void> {
    try {
      if (!id?.trim()) {
        throw new Error('Schedule ID is required');
      }

      const url = getApiUrl(endpoint.sentimentals.rejectSchedule(id));

      await api.post<StandardApiResponse>({
        url,
        auth: true,
      });

      showToast("Message rejected successfully", "success");
    } catch (error) {
      this.handleError(error, 'reject schedule');
    }
  }

  // Approve all messages for a specific date
  async approveAll(date: string): Promise<void> {
    try {
      if (!date?.trim()) {
        throw new Error('Date is required');
      }

      const url = getApiUrl(endpoint.sentimentals.approveAll);

      await api.post<StandardApiResponse>({
        url,
        body: { date },
        auth: true,
      });

      showToast("All messages approved successfully", "success");
    } catch (error) {
      this.handleError(error, 'approve all schedules');
    }
  }

  // Get sent message history
  async getLogs(params: GetLogsParams = {}): Promise<SentimentalLogsResponse['data']> {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append('status', params.status);
      if (params.occasionType) queryParams.append('occasionType', params.occasionType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const url = `${getApiUrl(endpoint.sentimentals.getLogs)}?${queryParams.toString()}`;

      const response = await api.get<SentimentalLogsResponse>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      const apiResponse = response as unknown as SentimentalLogsResponse;
      if (!apiResponse?.data) {
        throw new Error('Invalid response from sentimentals logs service');
      }

      return apiResponse.data;
    } catch (error) {
      this.handleError(error, 'fetch sentimental logs');
    }
  }

  // Test send a message
  async testSend(data: TestSendRequest): Promise<void> {
    try {
      if (!data.templateId?.trim()) {
        throw new Error('Template ID is required');
      }
      if (!data.leadId?.trim()) {
        throw new Error('Lead ID is required');
      }
      if (!data.occasionType) {
        throw new Error('Occasion type is required');
      }

      const url = getApiUrl(endpoint.sentimentals.testSend);

      await api.post<StandardApiResponse>({
        url,
        body: data,
        auth: true,
      });

      showToast("Test message sent successfully", "success");
    } catch (error) {
      this.handleError(error, 'send test message');
    }
  }

  // Update a scheduled message
  async updateScheduleMessage(scheduleId: string, message: string): Promise<SentimentalSchedule> {
    try {
      if (!scheduleId?.trim()) {
        throw new Error('Schedule ID is required');
      }
      if (!message?.trim()) {
        throw new Error('Message is required');
      }

      const url = getApiUrl(endpoint.sentimentals.updateScheduleMessage(scheduleId));

      const response = await api.put({
        url,
        body: { message },
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      const apiResponse = response as unknown as StandardApiResponse;
      if (!apiResponse?.data) {
        throw new Error('Invalid response from sentimentals service');
      }

      showToast("Message updated successfully", "success");
      return apiResponse.data;
    } catch (error) {
      this.handleError(error, 'update schedule message');
    }
  }

  // Get upcoming occasions (birthdays, policy anniversaries, holidays) for user's personal leads
  async getUpcomingOccasions(days: number = 60): Promise<UpcomingOccasion[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('days', days.toString());

      const url = `${getApiUrl(endpoint.sentimentals.getUpcomingOccasions)}?${queryParams.toString()}`;

      const response = await api.get<StandardApiResponse>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response is already the API body
      const apiResponse = response as unknown as { statusCode: number; message: string; data: UpcomingOccasion[]; timestamp: string };

      // Return the occasions array directly (or empty array if no data)
      return apiResponse?.data || [];
    } catch (error) {
      this.handleError(error, 'fetch upcoming occasions');
    }
  }
}

// Export singleton instance
export const sentimentalsService = new SentimentalsService();
export default sentimentalsService;
